import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { catalogMixin } from '@app/catalog/mixins/catalog-mixin';
import { filter, map, skipWhile, take, takeUntil } from 'rxjs/operators';
import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ChangeSetItemType, ItemChanges } from '@interfaces/change-sets/1.0';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { merge, Observable, of, Subject, Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  UntypedFormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { IsEqualService } from '@services/utilities/1.0/is-equal.service';
import { XenialService } from '@services/xenial/1.0/xenial.service';
import { ReportingCategory, Tenant } from '@generated/tcea/models';
import { BrandService } from '@services/brand/1.0/brand.service';
import { MessageType } from '@constants/message/1.0';
import { EventEmitter } from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { ChangeSetNoticeComponent } from '../../change-set-notice/change-set-notice.component';
import { FormSubmittedSyncDirective } from '../../../directives/form-submitted-sync/1.0/form-submitted-sync.directive';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'txp-catalog-xenial-details',
  templateUrl: './catalog-xenial-details.component.html',
  styleUrls: ['./catalog-xenial-details.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogXenialDetailsComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => CatalogXenialDetailsComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    MessageComponent,
    LoadingComponent,
    ChangeSetNoticeComponent,
    FormSubmittedSyncDirective,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatError,
    MatInput,
    TranslateModule,
  ],
})
export class CatalogXenialDetailsComponent
  extends catalogMixin(FormCanDeactivate)
  implements ControlValueAccessor, OnDestroy, AsyncValidator, OnInit, OnChanges
{
  @Input() declare itemChanges: ItemChanges;
  @Input() declare selectedVersion: ChangeSetItemType;
  @Input() brandId: string;
  @Input() catalogEntryId: string;
  @Input() formSubmitted: boolean;
  @Input() catalogType: 'PRODUCT' | 'MODIFIER' | 'MODIFIER_GROUP' = 'PRODUCT';
  @Output() xenialDetailsReady = new EventEmitter();

  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  loadingType = LoadingType;
  loadingColor = LoadingColor;
  messageType = MessageType;
  disabled: boolean;
  form: UntypedFormGroup;
  isEqual = IsEqualService.isEqual;
  onChangeFn: any;
  onTouchedFn: any;
  trackById = (_: number, opt: Option) => opt.id;

  // Dropdown data properties
  majorOptions: Option[] = [];
  minorOptions: Option[] = [];
  minorsByMajor: Record<string, Option[]> = {};

  // State properties
  xenialBrandIdSetupError = false;
  showLoadXenialError = false;
  isLoading = false;
  requireExternal = false;

  private unsubscribe = new Subject<void>();
  private majorChangeSub?: Subscription;

  constructor(
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private xenialService: XenialService,
    private brandService: BrandService,
  ) {
    super();
    this.form = this.formBuilder.group({
      majorCategoryId: new UntypedFormControl(''),
      minorCategoryId: new UntypedFormControl(''),
      rawPosValue: new UntypedFormControl('', {
        validators: [Validators.pattern('^\\d+(\\.\\d{1,2})?$')],
      }),
      enableXenialSync: new UntypedFormControl(false),
    });
    this.setupExternalFieldRequirements();
  }

  ngOnInit(): void {
    this.xenialDetailsReady.emit(false);
    this.loadBrandContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formSubmitted) {
      this.formSubmitted = changes.formSubmitted.currentValue;
      if (this.formSubmitted && this.form.invalid) {
        this.form.markAllAsTouched();
      }
    }

    // Reset form when switching to a different catalog entry
    if (changes.catalogEntryId) {
      if (!changes.catalogEntryId.isFirstChange()) {
        this.resetForm();
        this.xenialDetailsReady.emit(false);
      }
      // Reset error state when switching entries
      this.showLoadXenialError = false;
      // Load xenial details for the new catalog entry
      if (this.catalogEntryId) {
        this.loadXenialDetails();
      }
    }
  }

  loadBrandContext(): void {
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        filter((brand: Tenant) => brand.isSet),
        takeUntil(this.unsubscribe),
      )
      .subscribe((brand) => {
        let xenial: any = brand.aggregatorConfigurations?.xenial || null;
        let isXenialEnabled = xenial?.enabled || false;
        if (!isXenialEnabled) {
          this.xenialBrandIdSetupError = true;
          return;
        }
        let xenialBrandId = xenial.configurations?.brandId || null;
        this.populateXenialReportingCategories(brand.id, xenialBrandId);
        this.xenialBrandIdSetupError = false;
      });
  }

  populateXenialReportingCategories(brandId: string, xenialBrandId: string): void {
    this.xenialService
      .getReportingCategories(brandId, xenialBrandId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (data) => {
          this.initReportingSelectors(data);

          // Load xenial details after reporting categories are ready
          if (this.catalogEntryId) {
            this.loadXenialDetails();
          }
        },
        error: (e) => {
          this.showLoadXenialError = true;
        },
      });
  }

  loadXenialDetails(): void {
    if (!this.brandId || !this.catalogEntryId) {
      return;
    }

    this.isLoading = true;
    this.showLoadXenialError = false; // Reset error state when starting new load
    this.xenialService
      .getXenialDetails(this.brandId, this.catalogEntryId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (data) => {
          this.isInitialized = false; // Prevent change notifications during data loading
          this.form.patchValue(data || {});
          this.form.markAsPristine();

          // Manually trigger minor category population after data loading
          this.updateMinorOptions();

          this.isInitialized = true; // Re-enable change notifications
          this.isLoading = false;
          this.showLoadXenialError = false; // Ensure error state is cleared on success
          this.xenialDetailsReady.emit(true);
        },
        error: (error) => {
          this.isLoading = false;
          // Only show error if it's not a 404 - 404 is expected when no xenial details exist
          if (error?.status !== 404) {
            this.showLoadXenialError = true;
          } else {
            this.showLoadXenialError = false;
          }
          this.xenialDetailsReady.emit(false);
        },
      });
  }

  initReportingSelectors(apiRows: ReportingCategory[]): void {
    const idx = this.buildMajorMinorIndex(apiRows);
    this.majorOptions = idx.majors;
    this.minorsByMajor = idx.minorsByMajor;

    // controls (guard existence)
    const majorCtrl = this.form.get('majorCategoryId');
    const minorCtrl = this.form.get('minorCategoryId');

    // reset + disable minors initially (handles first load / new product)
    this.minorOptions = [];
    minorCtrl?.reset();
    minorCtrl?.disable({ emitEvent: false });

    this.majorChangeSub?.unsubscribe();

    // react to major changes; start with current value (possibly null)
    this.majorChangeSub = majorCtrl.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((majorId: string | null) => {
        const minors = (majorId && this.minorsByMajor[majorId]) || [];
        this.minorOptions = minors;

        if (!minors.length) {
          minorCtrl.reset();
          minorCtrl.disable({ emitEvent: false });
        } else {
          // keep current selection only if still valid
          if (!minors.some((m) => m.id === minorCtrl.value)) {
            minorCtrl.reset();
          }
          minorCtrl.enable({ emitEvent: false });
        }
      });

    // kick off once for the initial state without using startWith
    const initialMajor = (majorCtrl.value as string | null) ?? null;
    const initialMinors = (initialMajor && this.minorsByMajor[initialMajor]) || [];
    this.minorOptions = initialMinors;
    if (!initialMinors.length) {
      minorCtrl?.reset();
      minorCtrl?.disable({ emitEvent: false });
    } else {
      if (!initialMinors.some((m) => m.id === minorCtrl.value)) {
        minorCtrl.reset();
      }
      minorCtrl.enable({ emitEvent: false });
    }
  }

  updateMinorOptions(): void {
    const majorCtrl = this.form.get('majorCategoryId');
    const minorCtrl = this.form.get('minorCategoryId');

    if (!majorCtrl || !minorCtrl) {
      return;
    }

    const majorId = majorCtrl.value;
    const minors = (majorId && this.minorsByMajor[majorId]) || [];
    this.minorOptions = minors;

    if (!minors.length) {
      minorCtrl.reset();
      minorCtrl.disable({ emitEvent: false });
    } else {
      // Keep current selection only if still valid
      if (!minors.some((m) => m.id === minorCtrl.value)) {
        minorCtrl.reset();
      }
      minorCtrl.enable({ emitEvent: false });
    }
  }

  buildMajorMinorIndex(rows: ReportingCategory[] | null | undefined): {
    majors: Option[];
    minorsByMajor: Record<string, Option[]>;
  } {
    const data = Array.isArray(rows) ? rows : [];

    const byId = new Map<string, ReportingCategory>();
    for (const r of data) if (r?.externalEntityId) byId.set(r.externalEntityId, r);

    const majors: Option[] = data
      .filter((r) => !!r && r.isMajor)
      .map((r) => ({ id: r.externalEntityId, label: r.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const minorsAll = data
      .filter((r) => !!r && !r.isMajor)
      .map((r) => ({
        id: r.externalEntityId,
        label: r.name,
        majorId: r.majorReportingCategoryEntityId ?? null,
      }));

    const minorsByMajor: Record<string, Option[]> = {};

    for (const maj of majors) {
      const majorRow = byId.get(maj.id);

      // minors linked via backlink on minor
      const viaBack = minorsAll
        .filter((m) => m.majorId === maj.id)
        .map((m) => ({ id: m.id, label: m.label }));

      // minors linked via major.minorCategoryIds
      const viaList: Option[] = (majorRow?.minorCategoryIds ?? [])
        .map((id) => {
          const minorRow = byId.get(id);
          return minorRow && minorRow.isMajor === false
            ? { id: minorRow.externalEntityId, label: minorRow.name }
            : null;
        })
        .filter((x): x is Option => !!x);

      const merged = new Map<string, Option>();
      for (const m of viaBack) merged.set(m.id, m);
      for (const m of viaList) merged.set(m.id, m);
      minorsByMajor[maj.id] = Array.from(merged.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    return { majors, minorsByMajor };
  }

  ngOnDestroy(): void {
    this.majorChangeSub?.unsubscribe(); // prevent leaks when re-entering this screen
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges
      .pipe(
        // skipWhile(() => !this.isInitialized), // Skip value changes until component is initialized
        takeUntil(this.unsubscribe),
      )
      .subscribe((value) => {
        this.xenialSyncStatus();
        if (this.form.valid && this.onChangeFn && this.catalogEntryId) {
          let xenialDetails: any = {
            enableXenialSync: value.enableXenialSync || false,
            rawPosValue: value.rawPosValue,
            majorCategoryId: value.majorCategoryId,
            minorCategoryId: value.minorCategoryId,
            name: 'name',
            catalogType: this.catalogType,
          };
          // Add specific fields based on catalog type
          if (this.catalogType === 'MODIFIER_GROUP') {
            xenialDetails = {
              enableXenialSync: value.enableXenialSync || false,
              name: 'name',
              catalogType: this.catalogType,
            };
          }
          fn(xenialDetails);
        }
      });
  }

  xenialSyncStatus() {
    const major = this.form.get('majorCategoryId')!;
    const minor = this.form.get('minorCategoryId')!;
    const raw = this.form.get('rawPosValue')!;

    const nonEmpty = (v: any) => v != null && (typeof v === 'string' ? v.trim() !== '' : !!v);

    // For MODIFIER_GROUP, we don't need the xenial fields to be filled for sync to be enabled
    const compute = () => {
      if (this.catalogType === 'MODIFIER_GROUP') {
        return true; // Modifier groups can always be synced
      }
      return nonEmpty(major.value) && nonEmpty(minor.value) && nonEmpty(raw.value);
    };

    this.xenialDetailsReady.emit(!!compute());
    merge(major.valueChanges, minor.valueChanges, raw.valueChanges).subscribe(() =>
      this.xenialDetailsReady.emit(!!compute()),
    );
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {}

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (this.form?.status !== 'PENDING') {
      return of(
        this.form?.valid
          ? undefined
          : {
              invalidForm: {
                valid: false,
                message: this.translateService.instant('form.error.modifierGroupFieldsAreInvalid'),
              },
            },
      );
    } else {
      const obs = this.form?.statusChanges.pipe(
        takeUntil(this.unsubscribe),
        skipWhile((status) => status === 'PENDING'),
        take(1),
        map(() => {
          return this.form?.valid
            ? undefined
            : {
                invalidForm: {
                  valid: false,
                  message: this.translateService.instant(
                    'form.error.modifierGroupFieldsAreInvalid',
                  ),
                },
              };
        }),
      );
      return obs;
    }
  }

  private setupExternalFieldRequirements(): void {
    const majorCtrl = this.form.get('majorCategoryId')!;
    const minorCtrl = this.form.get('minorCategoryId')!;
    const rawCtrl = this.form.get('rawPosValue')!;

    const nonEmpty = (v: any) =>
      v !== null && v !== undefined && (typeof v === 'string' ? v.trim() !== '' : true);

    const apply = () => {
      // For MODIFIER_GROUP, we don't require external fields to be filled
      if (this.catalogType === 'MODIFIER_GROUP') {
        majorCtrl.setValidators(null);
        minorCtrl.setValidators(null);
        rawCtrl.setValidators([Validators.pattern('^\\d+(\\.\\d{1,2})?$')]);

        majorCtrl.updateValueAndValidity({ emitEvent: false });
        minorCtrl.updateValueAndValidity({ emitEvent: false });
        rawCtrl.updateValueAndValidity({ emitEvent: false });
        return;
      }

      const anyEntered = [majorCtrl.value, minorCtrl.value, rawCtrl.value].some(nonEmpty);

      if (anyEntered !== this.requireExternal) {
        this.requireExternal = anyEntered;

        majorCtrl.setValidators(anyEntered ? [Validators.required] : null);
        minorCtrl.setValidators(anyEntered ? [Validators.required] : null);

        // keep pattern always; add/remove required around it
        const pat = Validators.pattern('^\\d+(\\.\\d{1,2})?$');
        rawCtrl.setValidators(anyEntered ? [Validators.required, pat] : [pat]);

        majorCtrl.updateValueAndValidity({ emitEvent: false });
        minorCtrl.updateValueAndValidity({ emitEvent: false });
        rawCtrl.updateValueAndValidity({ emitEvent: false });
      }
    };

    // run once for initial state
    apply();

    // re-run whenever any of the three changes (only for non-modifier-group types)
    if (this.catalogType !== 'MODIFIER_GROUP') {
      merge(majorCtrl.valueChanges, minorCtrl.valueChanges, rawCtrl.valueChanges)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(() => apply());
    }
  }

  private resetForm(): void {
    this.form.reset();
    this.form.markAsUntouched();
    this.form.markAsPristine();
    // Clear any dropdowns
    this.minorOptions = [];
  }

  // Helper method to determine if external fields should be shown
  get shouldShowExternalFields(): boolean {
    return (
      this.catalogType === 'PRODUCT' ||
      this.catalogType === 'MODIFIER' ||
      this.catalogType === 'MODIFIER_GROUP'
    );
  }
}

export interface Option {
  id: string;
  label: string;
}
